import React, { type ReactNode } from "react";

import type { LowScoreCriteria } from "../../../Store/Reducers/MyPortfolioOverviewSlice";

const lsNumberInput =
  "w-12 rounded border border-gray-200 px-1.5 py-1 text-center text-xs outline-none disabled:bg-gray-50 disabled:text-gray-400";

const settingsNumberInput =
  "min-h-10 w-20 rounded-lg border border-[#dee2e6] bg-white px-2.5 py-2 text-sm font-medium text-gray-900 outline-none focus:border-[#974ae4] focus:shadow-[0_0_0_1px_#974ae4] disabled:cursor-not-allowed disabled:opacity-65";

const CriteriaRow = ({
  checked,
  onCheckedChange,
  children,
  compact,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  children: ReactNode;
  compact?: boolean;
}) => (
  <div
    className={`flex items-center gap-2 ${
      compact ? "mb-2 text-xs" : "mb-3 text-sm"
    } ${checked ? "text-gray-700" : "text-gray-400"}`}
  >
    <input
      type="checkbox"
      className={`shrink-0 cursor-pointer accent-purple-600 ${
        compact ? "h-3.5 w-3.5" : "h-4 w-4"
      }`}
      checked={checked}
      onChange={e => onCheckedChange(e.target.checked)}
    />
    <span className="flex min-w-0 flex-1 flex-wrap items-center gap-1">
      {children}
    </span>
  </div>
);

export interface LowScoreCriteriaFormProps {
  value: LowScoreCriteria;
  onChange: (patch: Partial<LowScoreCriteria>) => void;
  compact?: boolean;
}

const LowScoreCriteriaForm: React.FC<LowScoreCriteriaFormProps> = ({
  value,
  onChange,
  compact = false,
}) => {
  const numberInputClass = compact ? lsNumberInput : settingsNumberInput;

  return (
    <div>
      <CriteriaRow
        compact={compact}
        checked={value.renewal_enabled}
        onCheckedChange={checked => onChange({ renewal_enabled: checked })}
      >
        Renewal within
        <input
          type="number"
          min={1}
          max={365}
          className={numberInputClass}
          disabled={!value.renewal_enabled}
          value={value.renewal_days}
          onChange={e =>
            onChange({
              renewal_days: Number(e.target.value) || value.renewal_days,
            })
          }
        />
        days
        <span
          className={
            compact ? "text-[10.5px] text-gray-400" : "text-xs text-gray-500"
          }
        >
          AND RL below goal
        </span>
      </CriteriaRow>

      <CriteriaRow
        compact={compact}
        checked={value.rl_gap_enabled}
        onCheckedChange={checked => onChange({ rl_gap_enabled: checked })}
      >
        RL gap ≥
        <input
          type="number"
          min={1}
          max={5}
          className={numberInputClass}
          disabled={!value.rl_gap_enabled}
          value={value.rl_gap_levels}
          onChange={e =>
            onChange({
              rl_gap_levels: Number(e.target.value) || value.rl_gap_levels,
            })
          }
        />
        levels from goal
      </CriteriaRow>

      <CriteriaRow
        compact={compact}
        checked={value.assessment_stale_enabled}
        onCheckedChange={checked =>
          onChange({ assessment_stale_enabled: checked })
        }
      >
        Assessment older than
        <input
          type="number"
          min={1}
          max={365}
          className={numberInputClass}
          disabled={!value.assessment_stale_enabled}
          value={value.assessment_stale_days}
          onChange={e =>
            onChange({
              assessment_stale_days:
                Number(e.target.value) || value.assessment_stale_days,
            })
          }
        />
        days
      </CriteriaRow>

      <div className={`${compact ? "mb-1" : "mb-0"} text-gray-400`}>
        <div
          className={`flex items-center gap-2 ${
            compact ? "mb-2 text-xs" : "mb-3 text-sm"
          }`}
        >
          <input
            type="checkbox"
            className={`shrink-0 accent-purple-600 ${
              compact ? "h-3.5 w-3.5" : "h-4 w-4"
            }`}
            checked={false}
            disabled
            readOnly
            aria-label="No signal in days (not available)"
          />
          <span>No signal in {value.signal_recency_days} days</span>
        </div>
        <div className={compact ? "pl-6 text-[10px]" : "pl-6 text-xs"}>
          Not available yet — requires CRM/calendar access
        </div>
      </div>
    </div>
  );
};

export default LowScoreCriteriaForm;
